import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@erp/ui';
import type {
  PlatformAdminDashboard,
  PlatformAdminFeatures,
  PlatformAdminTenant,
  UpdatePlatformTenantInput,
} from '@erp/data-access/interfaces';
import {
  Bell,
  Building2,
  Check,
  Database,
  Download,
  Eye,
  ExternalLink,
  LayoutDashboard,
  Loader2,
  Package,
  Search,
  ShoppingBag,
  TrendingUp,
  Users,
} from '@/components/icons';
import {
  useCreatePlatformTenantView,
  usePlatformAdminDashboard,
  useSetPlatformTenantModule,
  useUpdatePlatformTenant,
} from '@/hooks/use-platform-admin';
import { useAuthService } from '@/lib/service-context';

const currency = new Intl.NumberFormat('fr-TN', {
  style: 'currency',
  currency: 'TND',
  maximumFractionDigits: 0,
});

const numberFormat = new Intl.NumberFormat('fr-TN');

const EMPTY_LANDING_USAGE: PlatformAdminDashboard['landing'] = {
  visits: 0,
  uniqueVisitors: 0,
  visits30d: 0,
  uniqueVisitors30d: 0,
  topPaths30d: [],
};

const ADMIN_TENANT_HANDOFF_STORAGE_KEY = 'gesty.adminTenantHandoff';
const ADMIN_TENANT_VIEW_STORAGE_KEY = 'gesty.adminTenantView';
const ADMIN_TENANT_AUTH_STORAGE_KEY = 'gesty.adminTenantAuth';
const ADMIN_TENANT_VIEW_TTL_MS = 60 * 60 * 1000;

const MODULES = [
  { key: 'sales', label: 'Ventes' },
  { key: 'purchases', label: 'Achats' },
  { key: 'stock', label: 'Stock' },
  { key: 'payments', label: 'Paiements' },
  { key: 'treasury', label: 'Tresorerie' },
  { key: 'checks', label: 'Cheques' },
  { key: 'withholding', label: 'RAS' },
  { key: 'storefront', label: 'Storefront' },
  { key: 'caisse', label: 'Caisse' },
  { key: 'pro', label: 'ProSpace' },
];

const TRIAL_EXTENSION_DAYS = [7, 14, 30] as const;

type StatusFilter = 'all' | 'active' | 'suspended' | 'trialing' | 'paid' | 'risk';
type PlatformAdminView = 'overview' | 'clients' | 'revenue' | 'storefront' | 'pro' | 'risks';

export function PlatformAdminDashboardPage() {
  return <PlatformAdminConsolePage view="overview" />;
}

export function PlatformAdminClientsPage() {
  return <PlatformAdminConsolePage view="clients" />;
}

export function PlatformAdminRevenuePage() {
  return <PlatformAdminConsolePage view="revenue" />;
}

export function PlatformAdminStorefrontPage() {
  return <PlatformAdminConsolePage view="storefront" />;
}

export function PlatformAdminProPage() {
  return <PlatformAdminConsolePage view="pro" />;
}

export function PlatformAdminRisksPage() {
  return <PlatformAdminConsolePage view="risks" />;
}

const PAGE_COPY: Record<PlatformAdminView, { title: string; subtitle: string }> = {
  overview: {
    title: 'Console SaaS Gesty',
    subtitle: 'Pilotage global des clients, modules, revenus, e-commerce, ProSpace, risques et activite.',
  },
  clients: {
    title: 'Clients SaaS',
    subtitle: 'Gestion complete des comptes, domaines, abonnements, modules et signaux d usage.',
  },
  revenue: {
    title: 'Revenus',
    subtitle: 'Facturation ERP, ventes storefront, paiements, achats et traction recente par client.',
  },
  storefront: {
    title: 'Storefront',
    subtitle: 'Acquisition e-commerce, catalogues publies, clients online, paniers et domaines boutique.',
  },
  pro: {
    title: 'ProSpace',
    subtitle: 'Suivi B2B des pros, commandes, commissions potentielles et validations en attente.',
  },
  risks: {
    title: 'Risques',
    subtitle: 'Clients suspendus, impayes, essais a convertir, validations pros et notifications a traiter.',
  },
};

function PlatformAdminConsolePage({ view }: { view: PlatformAdminView }) {
  const dashboard = usePlatformAdminDashboard();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const data = dashboard.data;
  const tenants = data?.tenants ?? [];
  const filteredTenants = useMemo(
    () => filterTenants(tenants, search, status),
    [search, status, tenants],
  );
  const riskTenants = useMemo(
    () =>
      filterTenants(tenants, search, status === 'all' ? 'risk' : status)
        .filter(isRiskTenant)
        .sort((a, b) => riskScore(b) - riskScore(a)),
    [search, status, tenants],
  );
  const selectionTenants = view === 'risks' ? riskTenants : filteredTenants;
  const selectedTenant =
    selectionTenants.find((tenant) => tenant.id === selectedId) ??
    selectionTenants[0] ??
    null;
  const page = PAGE_COPY[view];

  useEffect(() => {
    if (!selectedId && selectionTenants[0]) {
      setSelectedId(selectionTenants[0].id);
      return;
    }
    if (selectedId && !selectionTenants.some((tenant) => tenant.id === selectedId)) {
      setSelectedId(selectionTenants[0]?.id ?? null);
    }
  }, [selectedId, selectionTenants]);

  if (dashboard.isLoading) {
    return <PlatformAdminLoading />;
  }

  if (dashboard.isError) {
    return (
      <div className="flex min-h-[55vh] items-center justify-center">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>Console indisponible</CardTitle>
            <CardDescription>
              Les statistiques plateforme ne peuvent pas etre chargees pour cette session.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => dashboard.refetch()}>
              <Loader2 size={15} />
              Reessayer
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={page.title}
        subtitle={page.subtitle}
        actions={
          <Button variant="outline" size="sm" onClick={() => dashboard.refetch()}>
            <Loader2 size={15} />
            Actualiser
          </Button>
        }
      />

      {view === 'overview' ? <OverviewPageContent data={data} tenants={tenants} /> : null}

      {view === 'clients' ? (
        <ClientsPageContent
          data={data}
          tenants={filteredTenants}
          selectedTenant={selectedTenant}
          search={search}
          status={status}
          onSearchChange={setSearch}
          onStatusChange={setStatus}
          onSelect={setSelectedId}
        />
      ) : null}

      {view === 'revenue' ? (
        <RevenuePageContent
          data={data}
          tenants={filteredTenants}
          search={search}
          status={status}
          onSearchChange={setSearch}
          onStatusChange={setStatus}
        />
      ) : null}

      {view === 'storefront' ? (
        <StorefrontPageContent
          tenants={filteredTenants}
          search={search}
          status={status}
          onSearchChange={setSearch}
          onStatusChange={setStatus}
        />
      ) : null}

      {view === 'pro' ? (
        <ProPageContent
          tenants={filteredTenants}
          search={search}
          status={status}
          onSearchChange={setSearch}
          onStatusChange={setStatus}
        />
      ) : null}

      {view === 'risks' ? (
        <RisksPageContent
          data={data}
          tenants={riskTenants}
          selectedTenant={selectedTenant}
          search={search}
          status={status}
          onSearchChange={setSearch}
          onStatusChange={setStatus}
          onSelect={setSelectedId}
        />
      ) : null}
    </div>
  );
}

function OverviewPageContent({
  data,
  tenants,
}: {
  data: PlatformAdminDashboard | undefined;
  tenants: PlatformAdminTenant[];
}) {
  if (!data) {
    return <EmptyState text="Aucune donnee plateforme disponible." />;
  }

  return (
    <>
      <Overview data={data} />

      <section className="space-y-3">
        <SectionTitle
          icon={<TrendingUp size={18} />}
          title="Revenus et traction"
          description="Vue consolidee des factures, commandes en ligne et croissance recente."
        />
        <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <RevenueTimeline data={data} />
          <TopTenants tenants={tenants} />
        </div>
      </section>

      <LandingTrafficCard data={data} />

      <section className="grid gap-4 xl:grid-cols-2">
        <div className="space-y-3">
          <SectionTitle
            icon={<ShoppingBag size={18} />}
            title="Storefront"
            description="Meilleures boutiques par chiffre d affaires online."
          />
          <StorefrontGrid tenants={tenants} />
        </div>
        <div className="space-y-3">
          <SectionTitle
            icon={<Package size={18} />}
            title="ProSpace"
            description="Comptes B2B avec activite ou validation a suivre."
          />
          <ProGrid tenants={tenants} />
        </div>
      </section>

      <section className="space-y-3">
        <SectionTitle
          icon={<Bell size={18} />}
          title="Risques a traiter"
          description="Clients suspendus, essais proches de la fin, paiements en retard et notifications non lues."
        />
        <RiskTable tenants={tenants} />
      </section>
    </>
  );
}

function ClientsPageContent({
  data,
  tenants,
  selectedTenant,
  search,
  status,
  onSearchChange,
  onStatusChange,
  onSelect,
}: {
  data: PlatformAdminDashboard | undefined;
  tenants: PlatformAdminTenant[];
  selectedTenant: PlatformAdminTenant | null;
  search: string;
  status: StatusFilter;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: StatusFilter) => void;
  onSelect: (id: string) => void;
}) {
  return (
    <section className="space-y-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <SectionTitle
          icon={<Users size={18} />}
          title="Gestion des clients SaaS"
          description="Recherche, activation, statut d'abonnement, modules et signaux d'usage."
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadTenantsCsv('gesty-clients.csv', tenants)}
        >
          <Download size={15} />
          Export CSV
        </Button>
      </div>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(360px,0.6fr)]">
        <TenantTable
          tenants={tenants}
          selectedId={selectedTenant?.id ?? null}
          search={search}
          status={status}
          onSearchChange={onSearchChange}
          onStatusChange={onStatusChange}
          onSelect={onSelect}
        />
        {selectedTenant ? (
          <TenantManagementPanel tenant={selectedTenant} features={data?.features} />
        ) : (
          <EmptyPanel />
        )}
      </div>
      {selectedTenant ? <TenantDetailDashboard tenant={selectedTenant} /> : null}
    </section>
  );
}

function RevenuePageContent({
  data,
  tenants,
  search,
  status,
  onSearchChange,
  onStatusChange,
}: {
  data: PlatformAdminDashboard | undefined;
  tenants: PlatformAdminTenant[];
  search: string;
  status: StatusFilter;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: StatusFilter) => void;
}) {
  const sorted = [...tenants].sort(
    (a, b) =>
      b.revenue30d + b.invoiceRevenue + b.onlineRevenue -
      (a.revenue30d + a.invoiceRevenue + a.onlineRevenue),
  );
  const totals = sorted.reduce(
    (acc, tenant) => ({
      invoiceRevenue: acc.invoiceRevenue + tenant.invoiceRevenue,
      onlineRevenue: acc.onlineRevenue + tenant.onlineRevenue,
      revenue30d: acc.revenue30d + tenant.revenue30d,
      paymentTotal: acc.paymentTotal + tenant.paymentTotal,
    }),
    { invoiceRevenue: 0, onlineRevenue: 0, revenue30d: 0, paymentTotal: 0 },
  );

  return (
    <section className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <RevenueTimeline data={data} />
        <TopTenants tenants={tenants} />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <CardTitle>Revenus par client</CardTitle>
              <CardDescription>
                {sorted.length} client(s), {currency.format(totals.revenue30d)} sur les 30 derniers jours.
              </CardDescription>
            </div>
            <div className="flex flex-col gap-2 md:flex-row">
              <TenantFilterControls
                search={search}
                status={status}
                onSearchChange={onSearchChange}
                onStatusChange={onStatusChange}
                placeholder="Client, domaine, email"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadRevenueCsv('gesty-revenus.csv', sorted)}
              >
                <Download size={15} />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <Metric label="Factures ERP" value={currency.format(totals.invoiceRevenue)} />
            <Metric label="Storefront" value={currency.format(totals.onlineRevenue)} />
            <Metric label="30 jours" value={currency.format(totals.revenue30d)} />
            <Metric label="Paiements" value={currency.format(totals.paymentTotal)} />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Factures ERP</TableHead>
                <TableHead>Storefront</TableHead>
                <TableHead>30j</TableHead>
                <TableHead>Paiements</TableHead>
                <TableHead>Achats</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <EmptyState text="Aucun revenu ne correspond au filtre." />
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>
                      <TenantIdentity tenant={tenant} />
                    </TableCell>
                    <TableCell>{currency.format(tenant.invoiceRevenue)}</TableCell>
                    <TableCell>{currency.format(tenant.onlineRevenue)}</TableCell>
                    <TableCell className="font-semibold">{currency.format(tenant.revenue30d)}</TableCell>
                    <TableCell>{currency.format(tenant.paymentTotal)}</TableCell>
                    <TableCell>{currency.format(tenant.purchaseInvoiceTotal)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </section>
  );
}

function StorefrontPageContent({
  tenants,
  search,
  status,
  onSearchChange,
  onStatusChange,
}: {
  tenants: PlatformAdminTenant[];
  search: string;
  status: StatusFilter;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: StatusFilter) => void;
}) {
  const sorted = [...tenants].sort((a, b) => b.onlineRevenue - a.onlineRevenue);

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <SectionTitle
          icon={<ShoppingBag size={18} />}
          title="Storefront et acquisition"
          description="Commandes en ligne, clients boutique, paniers abandonnes et catalogue publie."
        />
        <div className="flex flex-col gap-2 md:flex-row">
          <TenantFilterControls
            search={search}
            status={status}
            onSearchChange={onSearchChange}
            onStatusChange={onStatusChange}
            placeholder="Boutique, domaine, proprietaire"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadStorefrontCsv('gesty-storefront.csv', sorted)}
          >
            <Download size={15} />
            Export CSV
          </Button>
        </div>
      </div>

      <StorefrontGrid tenants={sorted} limit={0} />
      <StorefrontTenantTable tenants={sorted} />
    </section>
  );
}

function ProPageContent({
  tenants,
  search,
  status,
  onSearchChange,
  onStatusChange,
}: {
  tenants: PlatformAdminTenant[];
  search: string;
  status: StatusFilter;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: StatusFilter) => void;
}) {
  const sorted = [...tenants].sort(
    (a, b) =>
      b.proOrderTotal + b.pendingProCustomers * 1000 -
      (a.proOrderTotal + a.pendingProCustomers * 1000),
  );

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <SectionTitle
          icon={<Package size={18} />}
          title="ProSpace"
          description="Suivi des pros, commandes B2B, commissions et validations en attente."
        />
        <div className="flex flex-col gap-2 md:flex-row">
          <TenantFilterControls
            search={search}
            status={status}
            onSearchChange={onSearchChange}
            onStatusChange={onStatusChange}
            placeholder="Client, domaine, email"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadProCsv('gesty-prospace.csv', sorted)}
          >
            <Download size={15} />
            Export CSV
          </Button>
        </div>
      </div>

      <ProGrid tenants={sorted} limit={0} />
      <ProTenantTable tenants={sorted} />
    </section>
  );
}

function RisksPageContent({
  data,
  tenants,
  selectedTenant,
  search,
  status,
  onSearchChange,
  onStatusChange,
  onSelect,
}: {
  data: PlatformAdminDashboard | undefined;
  tenants: PlatformAdminTenant[];
  selectedTenant: PlatformAdminTenant | null;
  search: string;
  status: StatusFilter;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: StatusFilter) => void;
  onSelect: (id: string) => void;
}) {
  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <SectionTitle
          icon={<Bell size={18} />}
          title="Risques a traiter"
          description="Clients suspendus, impayes, essais proches de la fin et validations en attente."
        />
        <div className="flex flex-col gap-2 md:flex-row">
          <TenantFilterControls
            search={search}
            status={status}
            onSearchChange={onSearchChange}
            onStatusChange={onStatusChange}
            placeholder="Client, domaine, email"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadRisksCsv('gesty-risques.csv', tenants)}
          >
            <Download size={15} />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
        <RiskTable tenants={tenants} selectedId={selectedTenant?.id ?? null} onSelect={onSelect} />
        {selectedTenant ? (
          <TenantManagementPanel tenant={selectedTenant} features={data?.features} />
        ) : (
          <EmptyPanel />
        )}
      </div>
    </section>
  );
}

function Overview({ data }: { data: PlatformAdminDashboard }) {
  const totals = data.totals;
  const landing = data.landing ?? EMPTY_LANDING_USAGE;
  const cards = [
    {
      label: 'Clients actifs',
      value: totals.activeTenants,
      detail: `${totals.tenants} clients au total`,
      icon: <Building2 size={18} />,
      tone: 'primary',
    },
    {
      label: 'Revenus 30j',
      value: currency.format(totals.revenue30d),
      detail: `${currency.format(totals.invoiceRevenue)} factures cumulees`,
      icon: <TrendingUp size={18} />,
      tone: 'success',
    },
    {
      label: 'Commandes online',
      value: totals.onlineOrders,
      detail: `${currency.format(totals.onlineRevenue)} via storefront`,
      icon: <ShoppingBag size={18} />,
      tone: 'info',
    },
    {
      label: 'Visiteurs storefront',
      value: numberFormat.format(totals.storefrontUniqueVisitors30d),
      detail: `${numberFormat.format(totals.storefrontVisits30d)} visites sur 30j`,
      icon: <Eye size={18} />,
      tone: 'info',
    },
    {
      label: 'Visiteurs landing',
      value: numberFormat.format(landing.uniqueVisitors30d),
      detail: `${numberFormat.format(landing.visits30d)} visites sur 30j`,
      icon: <LayoutDashboard size={18} />,
      tone: 'success',
    },
    {
      label: 'Espace utilise',
      value: formatBytes(totals.storageBytes),
      detail: `${numberFormat.format(totals.storageFiles)} fichiers clients`,
      icon: <Database size={18} />,
      tone: 'primary',
    },
    {
      label: 'Alertes',
      value: totals.pastDueTenants + totals.trialEndingSoon + totals.unreadNotifications,
      detail: `${totals.pastDueTenants} impayes, ${totals.trialEndingSoon} essais a surveiller`,
      icon: <Bell size={18} />,
      tone: 'warning',
    },
  ];

  return (
    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7">
      {cards.map((card) => (
        <Card key={card.label}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{card.label}</p>
                <p className="text-2xl font-semibold tracking-tight">{card.value}</p>
              </div>
              <div
                className={cn(
                  'flex size-10 items-center justify-center rounded-lg',
                  card.tone === 'success' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
                  card.tone === 'info' && 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
                  card.tone === 'warning' && 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
                  card.tone === 'primary' && 'bg-primary/10 text-primary',
                )}
              >
                {card.icon}
              </div>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">{card.detail}</p>
          </CardContent>
        </Card>
      ))}
    </section>
  );
}

function LandingTrafficCard({ data }: { data: PlatformAdminDashboard }) {
  const landing = data.landing ?? EMPTY_LANDING_USAGE;
  const paths = landing.topPaths30d ?? [];

  return (
    <section className="space-y-3">
      <SectionTitle
        icon={<LayoutDashboard size={18} />}
        title="Landing page"
        description="Trafic du site marketing Gesty sur 30 jours."
      />
      <Card>
        <CardContent className="grid gap-4 p-5 lg:grid-cols-[260px_1fr]">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
            <Metric label="Visiteurs uniques 30j" value={numberFormat.format(landing.uniqueVisitors30d)} />
            <Metric label="Visites 30j" value={numberFormat.format(landing.visits30d)} />
            <Metric label="Visiteurs total" value={numberFormat.format(landing.uniqueVisitors)} />
            <Metric label="Visites total" value={numberFormat.format(landing.visits)} />
          </div>
          <div className="space-y-2">
            <div className="text-sm font-semibold">Pages les plus vues</div>
            {paths.length === 0 ? (
              <EmptyState text="Aucune visite landing enregistree pour le moment." />
            ) : (
              <div className="space-y-2">
                {paths.map((path) => (
                  <div key={path.path} className="grid gap-2 rounded-lg border bg-background p-3 sm:grid-cols-[1fr_120px_120px] sm:items-center">
                    <div className="truncate text-sm font-medium">{path.path}</div>
                    <div className="text-sm text-muted-foreground">
                      {numberFormat.format(path.uniqueVisitors)} uniques
                    </div>
                    <div className="text-sm font-semibold sm:text-right">
                      {numberFormat.format(path.visits)} visites
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function RevenueTimeline({ data }: { data: PlatformAdminDashboard | undefined }) {
  const rows = data?.monthlyRevenue ?? [];
  const max = Math.max(
    1,
    ...rows.map((row) => row.invoiceRevenue + row.onlineRevenue),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Revenus mensuels</CardTitle>
        <CardDescription>Facturation ERP et ventes storefront sur les 12 derniers mois.</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyState text="Aucun revenu consolide pour le moment." />
        ) : (
          <div className="space-y-3">
            {rows.map((row) => {
              const total = row.invoiceRevenue + row.onlineRevenue;
              const width = Math.max(4, (total / max) * 100);
              return (
                <div key={row.month} className="grid gap-2 sm:grid-cols-[90px_1fr_110px] sm:items-center">
                  <div className="text-xs font-medium text-muted-foreground">
                    {formatMonth(row.month)}
                  </div>
                  <div className="h-8 overflow-hidden rounded-full bg-muted">
                    <div
                      className="flex h-full items-center rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground"
                      style={{ width: `${width}%` }}
                    >
                      {row.onlineOrders > 0 ? `${row.onlineOrders} online` : ''}
                    </div>
                  </div>
                  <div className="text-sm font-semibold sm:text-right">
                    {currency.format(total)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TopTenants({ tenants }: { tenants: PlatformAdminTenant[] }) {
  const top = [...tenants]
    .sort((a, b) => b.revenue30d - a.revenue30d)
    .slice(0, 6);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top clients 30j</CardTitle>
        <CardDescription>Classement par activite commerciale recente.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {top.length === 0 ? (
          <EmptyState text="Aucun client a classer." />
        ) : (
          top.map((tenant, index) => (
            <div key={tenant.id} className="flex items-center gap-3 rounded-lg border bg-background p-3">
              <div className="flex size-8 items-center justify-center rounded-md bg-primary/10 text-sm font-semibold text-primary">
                {index + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{tenant.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {tenant.subdomain}.gesty.tn
                </div>
              </div>
              <div className="text-right text-sm font-semibold">{currency.format(tenant.revenue30d)}</div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function TenantFilterControls({
  search,
  status,
  onSearchChange,
  onStatusChange,
  placeholder,
}: {
  search: string;
  status: StatusFilter;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: StatusFilter) => void;
  placeholder: string;
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <div className="relative">
        <Search size={15} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={placeholder}
          className="h-9 ps-9 sm:w-72"
        />
      </div>
      <Select value={status} onValueChange={(value) => onStatusChange(value as StatusFilter)}>
        <SelectTrigger className="h-9 sm:w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous</SelectItem>
          <SelectItem value="active">Actifs</SelectItem>
          <SelectItem value="suspended">Suspendus</SelectItem>
          <SelectItem value="trialing">Essais</SelectItem>
          <SelectItem value="paid">Payants</SelectItem>
          <SelectItem value="risk">A risque</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function TenantTable({
  tenants,
  selectedId,
  search,
  status,
  onSearchChange,
  onStatusChange,
  onSelect,
}: {
  tenants: PlatformAdminTenant[];
  selectedId: string | null;
  search: string;
  status: StatusFilter;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: StatusFilter) => void;
  onSelect: (id: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle>Clients</CardTitle>
            <CardDescription>{tenants.length} client(s) dans la vue courante.</CardDescription>
          </div>
          <TenantFilterControls
            search={search}
            status={status}
            onSearchChange={onSearchChange}
            onStatusChange={onStatusChange}
            placeholder="Rechercher client, domaine, email"
          />
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Etat</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Online</TableHead>
              <TableHead>Visiteurs</TableHead>
              <TableHead>Espace</TableHead>
              <TableHead>30j</TableHead>
              <TableHead>Derniere activite</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <EmptyState text="Aucun client ne correspond au filtre." />
                </TableCell>
              </TableRow>
            ) : (
              tenants.map((tenant) => (
                <TableRow
                  key={tenant.id}
                  onClick={() => onSelect(tenant.id)}
                  className={cn(
                    'cursor-pointer',
                    selectedId === tenant.id && 'bg-primary/8 hover:bg-primary/10',
                  )}
                >
                  <TableCell>
                    <div className="flex min-w-[220px] items-center gap-3">
                      <TenantAvatar tenant={tenant} />
                      <div className="min-w-0">
                        <div className="truncate font-medium">{tenant.name}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {tenant.subdomain}.gesty.tn
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <TenantStatus tenant={tenant} />
                      <span className="text-xs text-muted-foreground">{tenant.plan}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {numberFormat.format(tenant.salesDocuments)} docs
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {tenant.clients} clients, {tenant.articles} articles
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{numberFormat.format(tenant.onlineOrders)} commandes</div>
                    <div className="text-xs text-muted-foreground">
                      {numberFormat.format(tenant.publishedProducts)} produits
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {numberFormat.format(tenant.storefrontUniqueVisitors30d)} uniques
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {numberFormat.format(tenant.storefrontVisits30d)} visites 30j
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{formatBytes(tenant.storageBytes)}</div>
                    <div className="text-xs text-muted-foreground">
                      {numberFormat.format(tenant.storageFiles)} fichiers
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">{currency.format(tenant.revenue30d)}</TableCell>
                  <TableCell>{formatRelativeDate(tenant.lastActivityAt ?? tenant.updatedAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function TenantManagementPanel({
  tenant,
  features,
}: {
  tenant: PlatformAdminTenant;
  features?: PlatformAdminFeatures;
}) {
  const updateTenant = useUpdatePlatformTenant();
  const setModule = useSetPlatformTenantModule();
  const tenantView = useCreatePlatformTenantView();
  const authService = useAuthService();
  const [form, setForm] = useState<UpdatePlatformTenantInput>({});

  useEffect(() => {
    setForm({
      name: tenant.name,
      subdomain: tenant.subdomain,
      customDomain: tenant.customDomain ?? '',
      mf: tenant.mf ?? '',
      phone: tenant.phone ?? '',
      email: tenant.email ?? '',
      plan: tenant.plan,
      isActive: tenant.isActive,
      subscriptionStatus: tenant.subscriptionStatus ?? 'trialing',
      billingCycle: tenant.billingCycle ?? 'monthly',
      trialEndsAt: tenant.trialEndsAt,
      currentPeriodEnd: tenant.currentPeriodEnd,
      canceledAt: tenant.canceledAt,
    });
  }, [tenant]);

  const moduleMap = new Map(tenant.modules.map((module) => [module.key, module.enabled]));
  const subscriptionPending = updateTenant.isPending;
  const lifetimeAccess =
    tenant.subscriptionStatus === 'active' &&
    !tenant.currentPeriodEnd &&
    !tenant.canceledAt;

  const save = () => {
    const patch = buildTenantPatch(tenant, form, features);
    if (Object.keys(patch).length === 0) return;
    updateTenant.mutate({
      tenantId: tenant.id,
      patch,
    });
  };

  const applySubscriptionPatch = (patch: UpdatePlatformTenantInput) => {
    updateTenant.mutate({
      tenantId: tenant.id,
      patch,
    });
  };

  const makeLifetimeAccess = () => {
    applySubscriptionPatch({
      plan: 'pro',
      isActive: true,
      subscriptionStatus: 'active',
      billingCycle: 'yearly',
      trialEndsAt: null,
      currentPeriodEnd: null,
      canceledAt: null,
    });
  };

  const activateAnnualAccess = () => {
    applySubscriptionPatch({
      plan: 'pro',
      isActive: true,
      subscriptionStatus: 'active',
      billingCycle: 'yearly',
      trialEndsAt: null,
      currentPeriodEnd: addYears(new Date(), 1).toISOString(),
      canceledAt: null,
    });
  };

  const extendTrial = (days: number) => {
    const currentTrial = tenant.trialEndsAt ? new Date(tenant.trialEndsAt) : null;
    const base =
      currentTrial && Number.isFinite(currentTrial.getTime()) && currentTrial.getTime() > Date.now()
        ? currentTrial
        : new Date();
    applySubscriptionPatch({
      isActive: true,
      subscriptionStatus: 'trialing',
      billingCycle: form.billingCycle ?? tenant.billingCycle ?? 'monthly',
      trialEndsAt: addDays(base, days).toISOString(),
      currentPeriodEnd: null,
      canceledAt: null,
    });
  };

  const openTenantDashboard = async () => {
    const pendingWindow = typeof window !== 'undefined'
      ? window.open('about:blank', '_blank')
      : null;

    try {
      const sessionTokens = await authService.getSessionTokens();
      if (!sessionTokens?.accessToken) {
        throw new Error('No active platform admin session.');
      }

      const authPayload = {
        accessToken: sessionTokens.accessToken,
        expiresAt: toAdminAccessExpiresAt(sessionTokens.expiresAt),
      };
      const handoff = await tenantView.mutateAsync(tenant.id);
      if (pendingWindow) {
        storeTenantViewLaunchInWindow(pendingWindow, {
          tenantId: handoff.tenantId,
          viewExpiresAt: handoff.viewExpiresAt,
          auth: authPayload,
        });
      }
      const url = getTenantDashboardUrl({
        tenantId: handoff.tenantId,
        viewExpiresAt: handoff.viewExpiresAt,
        auth: authPayload,
      });

      if (pendingWindow) {
        try {
          pendingWindow.opener = null;
        } catch {
          // Non-critical: storage is already written in the child tab.
        }
        pendingWindow.location.replace(url);
        return;
      }

      if (typeof window !== 'undefined') {
        const opened = window.open(url, '_blank', 'noopener,noreferrer');
        if (!opened) window.location.assign(url);
      }
    } catch {
      pendingWindow?.close();
    }
  };

  return (
    <Card className="xl:sticky xl:top-4">
      <CardHeader>
        <div className="flex items-start gap-3">
          <TenantAvatar tenant={tenant} />
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate">{tenant.name}</CardTitle>
            <CardDescription className="truncate">
              {tenant.ownerEmail ?? tenant.email ?? 'Aucun email proprietaire'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
          <Field label="Nom">
            <Input
              value={form.name ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            />
          </Field>
          <Field label="Sous-domaine">
            <Input
              value={form.subdomain ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, subdomain: event.target.value }))}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.email ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
            />
          </Field>
          <Field label="Telephone">
            <Input
              value={form.phone ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
            />
          </Field>
          <Field label="Domaine custom">
            <Input
              value={form.customDomain ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, customDomain: event.target.value }))}
            />
          </Field>
          <Field label="Matricule fiscal">
            <Input
              value={form.mf ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, mf: event.target.value }))}
            />
          </Field>
          <Field label="Plan">
            <Select
              value={String(form.plan ?? 'free')}
              onValueChange={(value) => setForm((current) => ({ ...current, plan: value }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">free</SelectItem>
                <SelectItem value="pro">pro</SelectItem>
                <SelectItem value="enterprise">enterprise</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="rounded-lg border bg-muted/20 p-3 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold">Abonnement & acces</h3>
              <p className="text-xs text-muted-foreground">
                {lifetimeAccess
                  ? 'Acces permanent actif: aucune date de fin.'
                  : `Etat actuel: ${tenant.subscriptionStatus ?? 'aucun'}${
                      tenant.trialEndsAt ? ` · essai ${formatDate(tenant.trialEndsAt)}` : ''
                    }`}
              </p>
            </div>
            {lifetimeAccess ? <Badge variant="success">Lifetime</Badge> : null}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Statut">
              <Select
                value={String(form.subscriptionStatus ?? 'trialing')}
                disabled={features?.subscriptionStatusWrite === false}
                onValueChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    subscriptionStatus: value as UpdatePlatformTenantInput['subscriptionStatus'],
                  }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trialing">trialing</SelectItem>
                  <SelectItem value="active">active</SelectItem>
                  <SelectItem value="past_due">past_due</SelectItem>
                  <SelectItem value="canceled">canceled</SelectItem>
                  <SelectItem value="incomplete">incomplete</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Cycle">
              <Select
                value={String(form.billingCycle ?? 'monthly')}
                disabled={features?.subscriptionStatusWrite === false}
                onValueChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    billingCycle: value as UpdatePlatformTenantInput['billingCycle'],
                  }))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">monthly</SelectItem>
                  <SelectItem value="yearly">yearly</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Fin essai">
              <Input
                type="datetime-local"
                value={toDateTimeLocal(form.trialEndsAt)}
                disabled={features?.subscriptionStatusWrite === false}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    trialEndsAt: fromDateTimeLocal(event.target.value),
                  }))
                }
              />
            </Field>
            <Field label="Fin periode payee">
              <Input
                type="datetime-local"
                value={toDateTimeLocal(form.currentPeriodEnd)}
                disabled={features?.subscriptionStatusWrite === false}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    currentPeriodEnd: fromDateTimeLocal(event.target.value),
                  }))
                }
              />
            </Field>
            <Field label="Date annulation">
              <Input
                type="datetime-local"
                value={toDateTimeLocal(form.canceledAt)}
                disabled={features?.subscriptionStatusWrite === false}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    canceledAt: fromDateTimeLocal(event.target.value),
                  }))
                }
              />
            </Field>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="success"
              onClick={makeLifetimeAccess}
              disabled={subscriptionPending || features?.subscriptionStatusWrite === false}
            >
              <Check size={15} />
              Acces lifetime
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={activateAnnualAccess}
              disabled={subscriptionPending || features?.subscriptionStatusWrite === false}
            >
              Activer 1 an
            </Button>
            {TRIAL_EXTENSION_DAYS.map((days) => (
              <Button
                key={days}
                type="button"
                variant="outline"
                onClick={() => extendTrial(days)}
                disabled={subscriptionPending || features?.subscriptionStatusWrite === false}
              >
                +{days}j essai
              </Button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={updateTenant.isPending}>
            <Check size={15} />
            Enregistrer
          </Button>
          <Button
            variant={tenant.isActive ? 'destructive' : 'success'}
            onClick={() =>
              updateTenant.mutate({
                tenantId: tenant.id,
                patch: { isActive: !tenant.isActive },
              })
            }
            disabled={updateTenant.isPending}
          >
            {tenant.isActive ? 'Suspendre' : 'Reactiver'}
          </Button>
          <Button variant="outline" asChild>
            <a
              href={`https://${tenant.subdomain}.gesty.tn`}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink size={15} />
              Store
            </a>
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={openTenantDashboard}
            disabled={tenantView.isPending}
          >
            {tenantView.isPending ? <Loader2 size={15} className="animate-spin" /> : <LayoutDashboard size={15} />}
            Dashboard
          </Button>
        </div>

        {updateTenant.isError ? (
          <p className="text-sm text-destructive">
            {updateTenant.error instanceof Error ? updateTenant.error.message : 'Erreur de sauvegarde'}
          </p>
        ) : null}
        {tenantView.isError ? (
          <p className="text-sm text-destructive">
            {tenantView.error instanceof Error
              ? tenantView.error.message
              : 'Impossible d ouvrir ce tableau de bord admin.'}
          </p>
        ) : null}

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">Modules</h3>
            <Badge variant="outline">{tenant.enabledModules} actifs</Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            {MODULES.map((module) => {
              const enabled = moduleMap.get(module.key) ?? false;
              return (
                <Button
                  key={module.key}
                  type="button"
                  variant={enabled ? 'default' : 'outline'}
                  size="sm"
                  onClick={() =>
                    setModule.mutate({
                      tenantId: tenant.id,
                      moduleKey: module.key,
                      isEnabled: !enabled,
                    })
                  }
                  disabled={setModule.isPending}
                >
                  {module.label}
                </Button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <Metric label="Revenu 30j" value={currency.format(tenant.revenue30d)} />
          <Metric label="Factures" value={tenant.invoices} />
          <Metric label="Online" value={tenant.onlineOrders} />
          <Metric label="Pros" value={tenant.proCustomers} />
          <Metric label="Visiteurs 30j" value={numberFormat.format(tenant.storefrontUniqueVisitors30d)} />
          <Metric label="Espace" value={formatBytes(tenant.storageBytes)} />
        </div>

        <StorageBucketBreakdown tenant={tenant} compact />
      </CardContent>
    </Card>
  );
}

function TenantDetailDashboard({ tenant }: { tenant: PlatformAdminTenant }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Vue complete du client</CardTitle>
        <CardDescription>
          Activite ERP, storefront, ProSpace, stockage et trafic du client selectionne.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric label="Documents de vente" value={numberFormat.format(tenant.salesDocuments)} />
          <Metric label="Clients ERP" value={numberFormat.format(tenant.clients)} />
          <Metric label="Articles" value={numberFormat.format(tenant.articles)} />
          <Metric label="Equipe" value={numberFormat.format(tenant.teamMembers)} />
          <Metric label="CA factures" value={currency.format(tenant.invoiceRevenue)} />
          <Metric label="CA online" value={currency.format(tenant.onlineRevenue)} />
          <Metric label="CA 30j" value={currency.format(tenant.revenue30d)} />
          <Metric label="Paiements" value={currency.format(tenant.paymentTotal)} />
          <Metric label="Visiteurs uniques 30j" value={numberFormat.format(tenant.storefrontUniqueVisitors30d)} />
          <Metric label="Visites 30j" value={numberFormat.format(tenant.storefrontVisits30d)} />
          <Metric label="Visiteurs total" value={numberFormat.format(tenant.storefrontUniqueVisitors)} />
          <Metric label="Espace utilise" value={formatBytes(tenant.storageBytes)} />
          <Metric label="Produits publies" value={numberFormat.format(tenant.publishedProducts)} />
          <Metric label="Paniers ouverts" value={numberFormat.format(tenant.openAbandonedCarts)} />
          <Metric label="Pros approuves" value={numberFormat.format(tenant.approvedProCustomers)} />
          <Metric label="Commandes Pro" value={currency.format(tenant.proOrderTotal)} />
        </div>
        <StorageBucketBreakdown tenant={tenant} />
      </CardContent>
    </Card>
  );
}

function StorageBucketBreakdown({
  tenant,
  compact = false,
}: {
  tenant: PlatformAdminTenant;
  compact?: boolean;
}) {
  const buckets = tenant.storageBuckets.filter((bucket) => bucket.bytes > 0 || bucket.files > 0);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Stockage</h3>
        <Badge variant="outline">{formatBytes(tenant.storageBytes)}</Badge>
      </div>
      {buckets.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-muted/30 p-3 text-sm text-muted-foreground">
          Aucun fichier client detecte.
        </div>
      ) : (
        <div className={cn('grid gap-2', compact ? 'grid-cols-1' : 'sm:grid-cols-2 xl:grid-cols-3')}>
          {buckets.map((bucket) => (
            <div key={bucket.bucket} className="rounded-lg border bg-background p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium">{bucket.bucket}</span>
                <span className="text-sm font-semibold">{formatBytes(bucket.bytes)}</span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {numberFormat.format(bucket.files)} fichier(s)
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StorefrontGrid({
  tenants,
  limit = 6,
}: {
  tenants: PlatformAdminTenant[];
  limit?: number;
}) {
  const sorted = [...tenants]
    .filter(
      (tenant) =>
        tenant.onlineRevenue > 0 ||
        tenant.onlineOrders > 0 ||
        tenant.onlineCustomers > 0 ||
        tenant.storefrontVisits30d > 0 ||
        tenant.publishedProducts > 0 ||
        tenant.openAbandonedCarts > 0,
    )
    .sort((a, b) => b.onlineRevenue + b.storefrontVisits30d - (a.onlineRevenue + a.storefrontVisits30d));
  const top = limit > 0 ? sorted.slice(0, limit) : sorted;

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {top.length === 0 ? (
        <Card className="md:col-span-2 xl:col-span-3">
          <CardContent className="p-5">
            <EmptyState text="Aucune activite storefront pour le moment." />
          </CardContent>
        </Card>
      ) : (
        top.map((tenant) => (
          <Card key={tenant.id}>
            <CardContent className="space-y-4 p-5">
              <div className="flex items-center gap-3">
                <TenantAvatar tenant={tenant} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{tenant.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {tenant.onlineCustomers} clients online
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Metric label="CA online" value={currency.format(tenant.onlineRevenue)} />
                <Metric label="Commandes" value={tenant.onlineOrders} />
                <Metric label="Visiteurs 30j" value={numberFormat.format(tenant.storefrontUniqueVisitors30d)} />
                <Metric label="Produits" value={tenant.publishedProducts} />
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

function ProGrid({
  tenants,
  limit = 6,
}: {
  tenants: PlatformAdminTenant[];
  limit?: number;
}) {
  const sorted = tenants
    .filter((tenant) => tenant.proCustomers > 0 || tenant.proOrders > 0 || tenant.pendingProCustomers > 0)
    .sort((a, b) => b.proOrderTotal - a.proOrderTotal);
  const active = limit > 0 ? sorted.slice(0, limit) : sorted;

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {active.length === 0 ? (
        <Card className="md:col-span-2 xl:col-span-3">
          <CardContent className="p-5">
            <EmptyState text="Aucune activite ProSpace pour le moment." />
          </CardContent>
        </Card>
      ) : (
        active.map((tenant) => (
          <Card key={tenant.id}>
            <CardContent className="space-y-4 p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-medium">{tenant.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {tenant.approvedProCustomers} pros approuves
                  </div>
                </div>
                {tenant.pendingProCustomers > 0 ? (
                  <Badge variant="warning">{tenant.pendingProCustomers} en attente</Badge>
                ) : (
                  <Badge variant="secondary">Stable</Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Metric label="CA Pro" value={currency.format(tenant.proOrderTotal)} />
                <Metric label="Commandes" value={tenant.proOrders} />
                <Metric label="Pros" value={tenant.proCustomers} />
                <Metric label="Alertes" value={tenant.unreadNotifications} />
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

function StorefrontTenantTable({ tenants }: { tenants: PlatformAdminTenant[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Details storefront</CardTitle>
        <CardDescription>Domaines, visiteurs, commandes, clients, catalogue et paniers par client.</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Domaine boutique</TableHead>
              <TableHead>Commandes</TableHead>
              <TableHead>Visiteurs</TableHead>
              <TableHead>CA online</TableHead>
              <TableHead>Clients</TableHead>
              <TableHead>Produits</TableHead>
              <TableHead>Paniers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <EmptyState text="Aucune boutique ne correspond au filtre." />
                </TableCell>
              </TableRow>
            ) : (
              tenants.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>
                    <TenantIdentity tenant={tenant} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm">{tenant.storeCustomDomain ?? `${tenant.subdomain}.gesty.tn`}</span>
                      <Button variant="outline" size="sm" asChild className="w-fit">
                        <a
                          href={`https://${tenant.storeCustomDomain ?? `${tenant.subdomain}.gesty.tn`}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ExternalLink size={14} />
                          Ouvrir
                        </a>
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>{numberFormat.format(tenant.onlineOrders)}</TableCell>
                  <TableCell>
                    <div>{numberFormat.format(tenant.storefrontUniqueVisitors30d)} uniques</div>
                    <div className="text-xs text-muted-foreground">
                      {numberFormat.format(tenant.storefrontVisits30d)} visites 30j
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">{currency.format(tenant.onlineRevenue)}</TableCell>
                  <TableCell>
                    <div>{numberFormat.format(tenant.onlineCustomers)}</div>
                    <div className="text-xs text-muted-foreground">
                      LTV {currency.format(tenant.onlineCustomerLtv)}
                    </div>
                  </TableCell>
                  <TableCell>{numberFormat.format(tenant.publishedProducts)} publies</TableCell>
                  <TableCell>
                    <div>{numberFormat.format(tenant.openAbandonedCarts)} ouverts</div>
                    <div className="text-xs text-muted-foreground">
                      {currency.format(tenant.abandonedCartValue)}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ProTenantTable({ tenants }: { tenants: PlatformAdminTenant[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Details ProSpace</CardTitle>
        <CardDescription>Pros, validations, commandes B2B et alertes par client.</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Pros</TableHead>
              <TableHead>Validations</TableHead>
              <TableHead>Commandes</TableHead>
              <TableHead>CA Pro</TableHead>
              <TableHead>Alertes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <EmptyState text="Aucune activite ProSpace ne correspond au filtre." />
                </TableCell>
              </TableRow>
            ) : (
              tenants.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>
                    <TenantIdentity tenant={tenant} />
                  </TableCell>
                  <TableCell>{numberFormat.format(tenant.proCustomers)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge variant={tenant.pendingProCustomers > 0 ? 'warning' : 'secondary'}>
                        {tenant.pendingProCustomers} en attente
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {tenant.approvedProCustomers} approuves
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{numberFormat.format(tenant.proOrders)}</TableCell>
                  <TableCell className="font-semibold">{currency.format(tenant.proOrderTotal)}</TableCell>
                  <TableCell>{numberFormat.format(tenant.unreadNotifications)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function RiskTable({
  tenants,
  selectedId,
  onSelect,
}: {
  tenants: PlatformAdminTenant[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
}) {
  const risks = tenants.filter(isRiskTenant).sort((a, b) => riskScore(b) - riskScore(a));

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Risque</TableHead>
              <TableHead>Signal</TableHead>
              <TableHead>Derniere activite</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {risks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <EmptyState text="Aucun risque critique detecte." />
                </TableCell>
              </TableRow>
            ) : (
              risks.map((tenant) => (
                <TableRow
                  key={tenant.id}
                  onClick={() => onSelect?.(tenant.id)}
                  className={cn(
                    onSelect && 'cursor-pointer',
                    selectedId === tenant.id && 'bg-primary/8 hover:bg-primary/10',
                  )}
                >
                  <TableCell>
                    <div className="font-medium">{tenant.name}</div>
                    <div className="text-xs text-muted-foreground">{tenant.subdomain}.gesty.tn</div>
                  </TableCell>
                  <TableCell>{riskBadge(tenant)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{riskText(tenant)}</TableCell>
                  <TableCell>{formatRelativeDate(tenant.lastActivityAt ?? tenant.updatedAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function PlatformAdminLoading() {
  return (
    <div className="space-y-6">
      <div className="h-20 animate-pulse rounded-lg bg-muted" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="h-32 animate-pulse rounded-[1.25rem] bg-muted" />
        ))}
      </div>
      <div className="h-96 animate-pulse rounded-[1.25rem] bg-muted" />
    </div>
  );
}

function SectionTitle({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
      <div className="flex items-center gap-2">
        <span className="flex size-8 items-center justify-center rounded-md bg-primary/10 text-primary">
          {icon}
        </span>
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function TenantAvatar({ tenant }: { tenant: PlatformAdminTenant }) {
  return (
    <div
      className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-background text-sm font-semibold"
      style={tenant.primaryColor ? { color: tenant.primaryColor } : undefined}
    >
      {tenant.logoUrl ? (
        <img src={tenant.logoUrl} alt={tenant.name} className="size-full object-contain p-1" />
      ) : (
        tenant.name.charAt(0).toUpperCase()
      )}
    </div>
  );
}

function TenantIdentity({ tenant }: { tenant: PlatformAdminTenant }) {
  return (
    <div className="flex min-w-[220px] items-center gap-3">
      <TenantAvatar tenant={tenant} />
      <div className="min-w-0">
        <div className="truncate font-medium">{tenant.name}</div>
        <div className="truncate text-xs text-muted-foreground">
          {tenant.subdomain}.gesty.tn
        </div>
      </div>
    </div>
  );
}

function TenantStatus({ tenant }: { tenant: PlatformAdminTenant }) {
  if (!tenant.isActive) return <Badge variant="destructive">Suspendu</Badge>;
  if (tenant.subscriptionStatus === 'active') return <Badge variant="success">Payant</Badge>;
  if (tenant.subscriptionStatus === 'past_due') return <Badge variant="warning">Impayes</Badge>;
  if (tenant.subscriptionStatus === 'trialing') return <Badge variant="info">Essai</Badge>;
  return <Badge variant="secondary">Actif</Badge>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg border bg-background p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-semibold">{value}</div>
    </div>
  );
}

function EmptyPanel() {
  return (
    <Card>
      <CardContent className="p-6">
        <EmptyState text="Selectionnez un client pour le gerer." />
      </CardContent>
    </Card>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex min-h-24 items-center justify-center rounded-lg border border-dashed bg-muted/30 p-4 text-center text-sm text-muted-foreground">
      {text}
    </div>
  );
}

function filterTenants(
  tenants: PlatformAdminTenant[],
  search: string,
  status: StatusFilter,
): PlatformAdminTenant[] {
  const q = search.trim().toLowerCase();
  return tenants.filter((tenant) => {
    const haystack = [
      tenant.name,
      tenant.subdomain,
      tenant.customDomain,
      tenant.storeCustomDomain,
      tenant.email,
      tenant.ownerEmail,
      tenant.phone,
      tenant.mf,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const matchesSearch = !q || haystack.includes(q);
    if (!matchesSearch) return false;

    if (status === 'active') return tenant.isActive;
    if (status === 'suspended') return !tenant.isActive;
    if (status === 'trialing') return tenant.subscriptionStatus === 'trialing';
    if (status === 'paid') return tenant.subscriptionStatus === 'active';
    if (status === 'risk') return isRiskTenant(tenant);
    return true;
  });
}

function buildTenantPatch(
  tenant: PlatformAdminTenant,
  form: UpdatePlatformTenantInput,
  features?: PlatformAdminFeatures,
): UpdatePlatformTenantInput {
  const patch: UpdatePlatformTenantInput = {};
  const values = {
    name: clean(form.name),
    subdomain: clean(form.subdomain)?.toLowerCase(),
    customDomain: clean(form.customDomain)?.toLowerCase() ?? null,
    mf: clean(form.mf) ?? null,
    phone: clean(form.phone) ?? null,
    email: clean(form.email)?.toLowerCase() ?? null,
    plan: form.plan,
    isActive: form.isActive,
    subscriptionStatus: form.subscriptionStatus,
    billingCycle: form.billingCycle,
    trialEndsAt: form.trialEndsAt ?? null,
    currentPeriodEnd: form.currentPeriodEnd ?? null,
    canceledAt: form.canceledAt ?? null,
  };

  if (values.name && values.name !== tenant.name) patch.name = values.name;
  if (values.subdomain && values.subdomain !== tenant.subdomain) {
    patch.subdomain = values.subdomain;
  }
  if (values.customDomain !== (tenant.customDomain ?? null)) {
    patch.customDomain = values.customDomain;
  }
  if (values.mf !== (tenant.mf ?? null)) patch.mf = values.mf;
  if (values.phone !== (tenant.phone ?? null)) patch.phone = values.phone;
  if (values.email !== (tenant.email ?? null)) patch.email = values.email;
  if (values.plan !== undefined && values.plan !== tenant.plan) patch.plan = values.plan;
  if (values.isActive !== undefined && values.isActive !== tenant.isActive) {
    patch.isActive = values.isActive;
  }
  if (
    features?.subscriptionStatusWrite !== false &&
    values.subscriptionStatus !== undefined &&
    values.subscriptionStatus !== tenant.subscriptionStatus
  ) {
    patch.subscriptionStatus = values.subscriptionStatus;
  }
  if (
    features?.subscriptionStatusWrite !== false &&
    values.billingCycle !== undefined &&
    values.billingCycle !== tenant.billingCycle
  ) {
    patch.billingCycle = values.billingCycle;
  }
  if (
    features?.subscriptionStatusWrite !== false &&
    values.trialEndsAt !== (tenant.trialEndsAt ?? null)
  ) {
    patch.trialEndsAt = values.trialEndsAt;
  }
  if (
    features?.subscriptionStatusWrite !== false &&
    values.currentPeriodEnd !== (tenant.currentPeriodEnd ?? null)
  ) {
    patch.currentPeriodEnd = values.currentPeriodEnd;
  }
  if (
    features?.subscriptionStatusWrite !== false &&
    values.canceledAt !== (tenant.canceledAt ?? null)
  ) {
    patch.canceledAt = values.canceledAt;
  }

  return patch;
}

function clean(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

type TenantViewHandoffLaunch = {
  tenantId: string;
  viewExpiresAt: string;
  auth: {
    accessToken: string;
    expiresAt: string;
  };
};

function storeTenantViewLaunchInWindow(
  targetWindow: Window,
  launch: TenantViewHandoffLaunch,
): boolean {
  try {
    targetWindow.sessionStorage.removeItem(ADMIN_TENANT_HANDOFF_STORAGE_KEY);
    targetWindow.sessionStorage.setItem(
      ADMIN_TENANT_VIEW_STORAGE_KEY,
      JSON.stringify({
        tenantId: launch.tenantId,
        viewExpiresAt: launch.viewExpiresAt,
      }),
    );
    targetWindow.sessionStorage.setItem(ADMIN_TENANT_AUTH_STORAGE_KEY, JSON.stringify(launch.auth));
    return true;
  } catch {
    return false;
  }
}

function getTenantDashboardUrl(handoff: TenantViewHandoffLaunch): string {
  const baseUrl = `https://admin.gesty.tn/?admin_tenant_view=${Date.now()}`;
  const params = new URLSearchParams({
    admin_tenant_id: handoff.tenantId,
    admin_view_expires_at: handoff.viewExpiresAt,
    admin_access_token: handoff.auth.accessToken,
    admin_access_expires_at: handoff.auth.expiresAt,
  });
  return `${baseUrl}#${params.toString()}`;
}

function toAdminAccessExpiresAt(expiresAt: number | null): string {
  if (expiresAt && expiresAt * 1000 > Date.now()) {
    return new Date(expiresAt * 1000).toISOString();
  }
  return new Date(Date.now() + ADMIN_TENANT_VIEW_TTL_MS).toISOString();
}

function formatMonth(value: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('fr-TN', { month: 'short', year: '2-digit' });
}

function formatDate(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fr-TN');
}

function toDateTimeLocal(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join('-') + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDateTimeLocal(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addYears(date: Date, years: number): Date {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + years);
  return next;
}

function formatRelativeDate(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return "Aujourd'hui";
  if (diffDays === 1) return 'Hier';
  if (diffDays < 30) return `${diffDays} j`;
  return date.toLocaleDateString('fr-TN');
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toLocaleString('fr-TN', {
    maximumFractionDigits: value >= 10 || unit === 0 ? 0 : 1,
  })} ${units[unit]}`;
}

function isTrialEndingSoon(value: string | null): boolean {
  if (!value) return false;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return false;
  const days = (time - Date.now()) / (1000 * 60 * 60 * 24);
  return days >= 0 && days <= 3;
}

function isRiskTenant(tenant: PlatformAdminTenant): boolean {
  return (
    !tenant.isActive ||
    tenant.subscriptionStatus === 'past_due' ||
    isTrialEndingSoon(tenant.trialEndsAt) ||
    tenant.unreadNotifications > 0 ||
    tenant.pendingProCustomers > 0
  );
}

function riskScore(tenant: PlatformAdminTenant): number {
  let score = 0;
  if (!tenant.isActive) score += 50;
  if (tenant.subscriptionStatus === 'past_due') score += 40;
  if (isTrialEndingSoon(tenant.trialEndsAt)) score += 20;
  score += Math.min(tenant.unreadNotifications, 20);
  score += tenant.pendingProCustomers * 2;
  return score;
}

function riskBadge(tenant: PlatformAdminTenant) {
  if (!tenant.isActive) return <Badge variant="destructive">Suspendu</Badge>;
  if (tenant.subscriptionStatus === 'past_due') return <Badge variant="warning">Paiement</Badge>;
  if (isTrialEndingSoon(tenant.trialEndsAt)) return <Badge variant="warning">Essai</Badge>;
  if (tenant.pendingProCustomers > 0) return <Badge variant="info">Pros</Badge>;
  return <Badge variant="secondary">Notifications</Badge>;
}

function riskText(tenant: PlatformAdminTenant): string {
  if (!tenant.isActive) return 'Client desactive dans la plateforme.';
  if (tenant.subscriptionStatus === 'past_due') return 'Abonnement en retard de paiement.';
  if (isTrialEndingSoon(tenant.trialEndsAt)) return 'Essai a convertir dans les 3 prochains jours.';
  if (tenant.pendingProCustomers > 0) return `${tenant.pendingProCustomers} pro(s) en attente de validation.`;
  return `${tenant.unreadNotifications} notification(s) non lue(s).`;
}

function downloadTenantsCsv(filename: string, tenants: PlatformAdminTenant[]) {
  downloadCsv(
    filename,
    tenants.map((tenant) => ({
      client: tenant.name,
      subdomain: `${tenant.subdomain}.gesty.tn`,
      customDomain: tenant.customDomain,
      ownerEmail: tenant.ownerEmail ?? tenant.email,
      phone: tenant.phone,
      plan: tenant.plan,
      subscriptionStatus: tenant.subscriptionStatus,
      billingCycle: tenant.billingCycle,
      trialEndsAt: tenant.trialEndsAt,
      currentPeriodEnd: tenant.currentPeriodEnd,
      lifetimeAccess: tenant.subscriptionStatus === 'active' && !tenant.currentPeriodEnd && !tenant.canceledAt,
      active: tenant.isActive,
      modules: tenant.modules.filter((module) => module.enabled).map((module) => module.key).join('|'),
      teamMembers: tenant.teamMembers,
      clients: tenant.clients,
      articles: tenant.articles,
      storageBytes: tenant.storageBytes,
      storageFiles: tenant.storageFiles,
      storefrontVisits30d: tenant.storefrontVisits30d,
      storefrontUniqueVisitors30d: tenant.storefrontUniqueVisitors30d,
      storefrontVisits: tenant.storefrontVisits,
      storefrontUniqueVisitors: tenant.storefrontUniqueVisitors,
      revenue30d: tenant.revenue30d,
      lastActivityAt: tenant.lastActivityAt ?? tenant.updatedAt,
    })),
  );
}

function downloadRevenueCsv(filename: string, tenants: PlatformAdminTenant[]) {
  downloadCsv(
    filename,
    tenants.map((tenant) => ({
      client: tenant.name,
      subdomain: `${tenant.subdomain}.gesty.tn`,
      invoiceRevenue: tenant.invoiceRevenue,
      onlineRevenue: tenant.onlineRevenue,
      revenue30d: tenant.revenue30d,
      payments: tenant.payments,
      paymentTotal: tenant.paymentTotal,
      purchaseDocuments: tenant.purchaseDocuments,
      purchaseInvoiceTotal: tenant.purchaseInvoiceTotal,
      salesDocuments: tenant.salesDocuments,
      invoices: tenant.invoices,
    })),
  );
}

function downloadStorefrontCsv(filename: string, tenants: PlatformAdminTenant[]) {
  downloadCsv(
    filename,
    tenants.map((tenant) => ({
      client: tenant.name,
      subdomain: `${tenant.subdomain}.gesty.tn`,
      storeDomain: tenant.storeCustomDomain ?? `${tenant.subdomain}.gesty.tn`,
      onlineOrders: tenant.onlineOrders,
      onlineRevenue: tenant.onlineRevenue,
      storefrontVisits30d: tenant.storefrontVisits30d,
      storefrontUniqueVisitors30d: tenant.storefrontUniqueVisitors30d,
      storefrontVisits: tenant.storefrontVisits,
      storefrontUniqueVisitors: tenant.storefrontUniqueVisitors,
      onlineCustomers: tenant.onlineCustomers,
      onlineCustomerLtv: tenant.onlineCustomerLtv,
      storefrontProducts: tenant.storefrontProducts,
      publishedProducts: tenant.publishedProducts,
      openAbandonedCarts: tenant.openAbandonedCarts,
      abandonedCartValue: tenant.abandonedCartValue,
      whatsappEnabled: tenant.whatsappEnabled,
    })),
  );
}

function downloadProCsv(filename: string, tenants: PlatformAdminTenant[]) {
  downloadCsv(
    filename,
    tenants.map((tenant) => ({
      client: tenant.name,
      subdomain: `${tenant.subdomain}.gesty.tn`,
      proCustomers: tenant.proCustomers,
      approvedProCustomers: tenant.approvedProCustomers,
      pendingProCustomers: tenant.pendingProCustomers,
      proOrders: tenant.proOrders,
      proOrderTotal: tenant.proOrderTotal,
      unreadNotifications: tenant.unreadNotifications,
    })),
  );
}

function downloadRisksCsv(filename: string, tenants: PlatformAdminTenant[]) {
  downloadCsv(
    filename,
    tenants.map((tenant) => ({
      client: tenant.name,
      subdomain: `${tenant.subdomain}.gesty.tn`,
      risk: riskText(tenant),
      active: tenant.isActive,
      subscriptionStatus: tenant.subscriptionStatus,
      trialEndsAt: tenant.trialEndsAt,
      pendingProCustomers: tenant.pendingProCustomers,
      unreadNotifications: tenant.unreadNotifications,
      lastActivityAt: tenant.lastActivityAt ?? tenant.updatedAt,
    })),
  );
}

function downloadCsv(filename: string, rows: Array<Record<string, unknown>>) {
  if (typeof document === 'undefined') return;
  const headers = rows[0] ? Object.keys(rows[0]) : ['empty'];
  const csv = [
    headers.join(','),
    ...rows.map((row) => headers.map((header) => formatCsvValue(row[header])).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function formatCsvValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  if (!/[",\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}
